/**
 * Authentication context.
 *
 * The principal — including its permission list — always comes from `/auth/me`, never from the
 * token payload. The frontend hides what the user cannot do; the backend refuses it regardless.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

import { auth } from '@/api/endpoints';
import {
  ApiError,
  getAccessToken,
  setAccessToken,
  setTokenSource,
  setUnauthorizedHandler,
} from '@/api/client';
import { IS_LOCAL_AUTH } from '@/auth/identityProvider';
import { beginSignIn, completeSignIn, endSignIn, silentToken } from '@/auth/entra';
import type { CurrentUser, Permission, RoleName } from '@/types';

interface AuthContextValue {
  user: CurrentUser | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  /** Set when Entra authenticated somebody the application does not recognise. */
  rejection: string | null;
  /** Local builds only: the seeded-account sign-in. */
  signIn: (email: string) => Promise<CurrentUser>;
  /** Entra builds only: hands off to Microsoft. Does not return — the page navigates away. */
  signInWithMicrosoft: () => Promise<void>;
  signOut: () => Promise<void>;
  can: (...permissions: Permission[]) => boolean;
  canAny: (...permissions: Permission[]) => boolean;
  hasRole: (...roles: RoleName[]) => boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }): JSX.Element {
  const [user, setUser] = useState<CurrentUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  /** Why a sign-in that reached Entra did not end in a session here. */
  const [rejection, setRejection] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function restore(): Promise<void> {
      // Under Entra the token is Microsoft's, not ours, so a redirect landing now has to be
      // finished before anything asks who the user is — and a reload with a live session has to
      // reach for a silent token rather than concluding nobody is signed in.
      // A local sign-in kept in this tab is used as it is; otherwise a Microsoft session is
      // finished or restored, and from then on every call asks MSAL for its token — which
      // renews it, rather than holding the one from page load until it expires.
      if (!IS_LOCAL_AUTH && !getAccessToken()) {
        try {
          const token = await completeSignIn();
          if (token) {
            setAccessToken(token);
            setTokenSource(({ refresh }) => silentToken(refresh));
          }
        } catch {
          // A failed redirect is not a crash: fall through and the sign-in screen asks again.
        }
      }
      if (!getAccessToken()) {
        setIsLoading(false);
        return;
      }
      try {
        const current = await auth.me();
        if (!cancelled) {
          setUser(current);
          setRejection(null);
        }
      } catch (error) {
        // Why this failed is the whole message. Signing in with Microsoft and landing back on the
        // sign-in screen with nothing said is indistinguishable from the sign-in itself failing —
        // it cost a real diagnosis on the first deployed environment, where the identity was fine
        // and the application simply had no such user.
        setAccessToken(null);
        setTokenSource(null);
        if (!cancelled) {
          setUser(null);
          setRejection(
            error instanceof ApiError
              ? error.message
              : 'Signed in, but this application could not confirm the account.',
          );
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }

    void restore();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    setUnauthorizedHandler(() => setUser(null));
    return () => setUnauthorizedHandler(null);
  }, []);

  const signIn = useCallback(async (email: string): Promise<CurrentUser> => {
    const result = await auth.localLogin(email);
    // A local session's token is the one returned here, not Microsoft's.
    setTokenSource(null);
    setAccessToken(result.accessToken);
    setUser(result.user);
    return result.user;
  }, []);

  const signInWithMicrosoft = useCallback(async (): Promise<void> => {
    await beginSignIn();
  }, []);

  const signOut = useCallback(async (): Promise<void> => {
    try {
      await auth.logout();
    } catch {
      // Signing out locally matters more than recording it; the token expires regardless.
    }
    setAccessToken(null);
    setTokenSource(null);
    setUser(null);
    if (!IS_LOCAL_AUTH) {
      // Clearing our own session while leaving the Entra one open means the next sign-in goes
      // straight back in without asking, which does not look like signing out.
      await endSignIn();
    }
  }, []);

  const value = useMemo<AuthContextValue>(() => {
    const granted = new Set<string>(user?.permissions ?? []);
    return {
      user,
      isLoading,
      isAuthenticated: user !== null,
      rejection,
      signIn,
      signInWithMicrosoft,
      signOut,
      can: (...permissions) => permissions.every((p) => granted.has(p)),
      canAny: (...permissions) => permissions.some((p) => granted.has(p)),
      hasRole: (...roles) => roles.some((role) => user?.roles.includes(role) ?? false),
    };
  }, [user, isLoading, rejection, signIn, signInWithMicrosoft, signOut]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used inside an AuthProvider.');
  return context;
}
